import { useState, useEffect, startTransition } from 'react';
import { useLocation } from 'react-router-dom';
import { fIsPatientUser, fIsUserLoggedIn } from 'lib/appFx';
import { fetchCurrentUserProfile } from 'services/niuHealthApi';
import usePatientPopupFlow from './usePatientPopupFlow';

/**
 * Main hook for NIU Social popup management
 * Combines user authentication check, profile fetching, and popup display logic
 * 
 * Usage: Call this hook in components that need popup enforcement (/settings, /submit, /leaderboard)
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.useServiceMessage - Use service-specific subscription message
 * @param {boolean} options.enabled - Enable/disable popup functionality (default: true)
 * @returns {Object} Hook state and utility functions
 */
const useNiuSocialPopups = (options = {}) => {
  const { useServiceMessage = false, enabled = true } = options;
  const location = useLocation();
  
  const [patientDetails, setPatientDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Determine if we should check for popups based on current page
  const restrictedPages = ['/settings', '/submit', '/leaderboard'];
  const isOnRestrictedPage = restrictedPages.includes(location.pathname);
  const isHomePage = location.pathname === '/home' || location.pathname === '/';

  // Check if user qualifies for popup checks (synchronous checks only)
  const shouldCheckPopups = () => {
    if (!enabled || isHomePage) {
      return false;
    }

    // Only proceed if user is logged in, is a patient, and is on a restricted page
    try {
      const isLoggedIn = fIsUserLoggedIn();
      const isPatient = fIsPatientUser();
      
      return isLoggedIn && isPatient && isOnRestrictedPage;
    } catch (err) {
      console.error('Error checking user eligibility for popups:', err);
      return false;
    }
  };

  // Fetch user profile when needed
  useEffect(() => {
    let isMounted = true;
    let timeoutId;

    const fetchProfile = async () => {
      if (!shouldCheckPopups()) {
        setPatientDetails(null);
        setLoading(false);
        return;
      }

      // Use startTransition to avoid React Suspense issues
      timeoutId = setTimeout(() => {
        if (!isMounted) return;
        
        startTransition(async () => {
          setLoading(true);
          setError(null);

          try {
            const profileResponse = await fetchCurrentUserProfile();
            
            if (isMounted) {
              if (profileResponse.status) {
                setPatientDetails(profileResponse);
                setError(null);
              } else {
                console.error('Failed to fetch profile:', profileResponse.error);
                setError(profileResponse.error);
                setPatientDetails(null);
              }
            }
          } catch (err) {
            if (isMounted) {
              console.error('Error fetching user profile for popup check:', err);
              setError(err.message || 'Failed to fetch user profile');
              setPatientDetails(null);
            }
          } finally {
            if (isMounted) {
              setLoading(false);
            }
          }
        });
      }, 100); // Small delay to avoid suspense issues
    };

    fetchProfile();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [location.pathname, enabled]);

  // Use the popup flow hook only if we have data and should show popups
  // Pass null if no patientDetails to prevent suspension issues
  const popupFlow = usePatientPopupFlow(
    (shouldCheckPopups() && patientDetails?.status) ? patientDetails : null, 
    [location.pathname], 
    { useServiceMessage }
  );

  // Return hook state and utilities
  return {
    // State
    loading,
    error,
    patientDetails,
    isOnRestrictedPage,
    isHomePage,
    shouldCheckPopups: shouldCheckPopups(),
    
    // From popup flow hook
    shouldShowPopup: popupFlow.shouldShowPopup,
    getNextPopup: popupFlow.getNextPopup,
    getCompletionStatus: popupFlow.getCompletionStatus,
    
    // Utility functions
    isPopupSystemActive: () => {
      return enabled && shouldCheckPopups();
    },
    
    refetchProfile: async () => {
      if (shouldCheckPopups()) {
        startTransition(async () => {
          setLoading(true);
          try {
            const profileResponse = await fetchCurrentUserProfile();
            setPatientDetails(profileResponse.status ? profileResponse : null);
            setError(profileResponse.status ? null : profileResponse.error);
          } catch (err) {
            setError(err.message);
            setPatientDetails(null);
          } finally {
            setLoading(false);
          }
        });
      }
    }
  };
};

export default useNiuSocialPopups;